import React, { useMemo } from 'react'
import css from './monthViewCalendar.module.css'
import timeUtils from '@/utils/time.utils'
import { IMaintenanceEvent, IMaintenanceEventFilters, TMaintenanceEventType } from '../calendar.types'
import calendarUtils from '../calendar.utils'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'

interface IProps {
  selectedDates: IMaintenanceEventFilters
  events: IMaintenanceEvent[]
  onCellClick: (date: Date) => void
  isLoaded: boolean
}

const CalendarMonthView = (props: IProps) => {
  const currentMonth = useMemo(() => {
    return calendarUtils.getCurrentMonth('month', props.selectedDates)
  }, [props.selectedDates])

  const monthGrid = useMemo(() => {
    return timeUtils.eachDayOfInterval({ start: props.selectedDates.date_gte, end: props.selectedDates.date_lte })
  }, [props.selectedDates])

  const eventsByDate = useMemo((): Map<string, Record<TMaintenanceEventType, number>> => {
    return props.events.reduce((acc, item) => {
      const key = timeUtils.formatDate(item.ts, 'yyyy-MM-dd')
      if (!acc.has(key)) {
        acc.set(key, {})
      }
      if (!acc.get(key)[item.type]) acc.get(key)[item.type] = 0
      acc.get(key)[item.type]++
      return acc
    }, new Map())
  }, [props.events])

  const renderCalendarCell = (date: Date) => {
    const eventCountByType = eventsByDate.get(timeUtils.formatDate(date, 'yyyy-MM-dd'))
    return (
      <div className={css.cellDay} data-current-month={date.getMonth() === currentMonth.getMonth()}>
        <div className={css.cellDayText} data-current-day={timeUtils.isToday(date)}>
          {date.getDate()}
        </div>
        {eventCountByType && (
          <div className={css.cellDayEvents}>
            {Object.keys(eventCountByType).map((key) => (
              <div className={css.cellEventBadge} data-event-type={key} key={key}>
                {eventCountByType[key]}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderCol = () => {
    return Array.from({ length: 6 }).map((_, weekIndex) => (
      <div key={weekIndex} className={css.calendarRow} id={weekIndex.toString()}>
        {Array.from({ length: 7 }).map((_, dayIndex) => (
          <div
            key={dayIndex + weekIndex * 7}
            className={css.cell}
            onClick={() => props.onCellClick(monthGrid[dayIndex + weekIndex * 7])}
          >
            {renderCalendarCell(monthGrid[dayIndex + weekIndex * 7])}
          </div>
        ))}
      </div>
    ))
  }

  const renderCalendarHeader = () => {
    return Array.from({ length: 7 }).map((_, index) => (
      <div className={css.cellDay} style={{ paddingRight: '12px' }} key={index}>
        <span className={css.cellDayText}>{timeUtils.formatDate(monthGrid[index], 'EEEEEE')}</span>
      </div>
    ))
  }

  if (!props.isLoaded)
    return (
      <div className="height100 flex-lines center flex-lines center">
        <ThreeDotsLoader />
      </div>
    )

  return (
    <div className={css.container}>
      <div className={css.calendarHeaderRow}>{renderCalendarHeader()}</div>
      <div className={css.calendarTable}>{renderCol()}</div>
    </div>
  )
}

export default React.memo(CalendarMonthView)
